import { useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  createChunks,
  fileReducer,
  FileSelection,
  GoogleFontIcon,
  Modal,
  Upload,
} from '@asset-metrix/portal-ui';

import { uploadZipArchive } from '../../api/portalGateway/fileUpload';
import { extractErrorMessage } from '../../utils/errorMessage';

const chunkSize = 20;

export type ZipUploadModalProps = {
  contentId?: string;
  onClose: () => void;
  onUploadSuccess?: () => Promise<unknown>;
  opened: boolean;
  withinPortal?: boolean;
};

export function ZipUploadModal({
  contentId,
  onClose,
  onUploadSuccess,
  opened,
  withinPortal,
}: ZipUploadModalProps) {
  const { t } = useTranslation();
  const [fileState, dispatch] = useReducer(fileReducer, []);

  const uploadFile = useCallback(
    async (file: FileSelection) => {
      try {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: file.id, newState: 'isPending' },
        });
        const res = await uploadZipArchive(file.file, contentId);

        if (res.status === 'success') {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: { id: file.id, newState: 'isSuccess' },
          });
          return true;
        } else {
          dispatch({
            type: 'updateFileSelectionUploadState',
            payload: {
              id: file.id,
              newState: 'isError',
              error: extractErrorMessage(res),
            },
          });
          return false;
        }
      } catch {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: file.id, newState: 'isError' },
        });
        return false;
      }
    },
    [contentId]
  );

  const uploadSingleFile = useCallback(
    async (file: FileSelection) => {
      const success = await uploadFile(file);
      if (success) {
        await onUploadSuccess?.();
      }
    },
    [uploadFile, onUploadSuccess]
  );

  const cleanupAndClose = () => {
    dispatch({ type: 'resetFileSelections' });
    onClose();
  };

  const uploadAllFiles = useCallback(async () => {
    const filesToUpload = fileState.filter(
      (selection) =>
        selection.uploadState === 'initial' ||
        selection.uploadState === 'isError'
    );

    try {
      let hasSuccess = false;
      for (const chunk of createChunks(filesToUpload, chunkSize)) {
        const results = await Promise.all(
          chunk.map((selection) => uploadFile(selection))
        );
        hasSuccess = hasSuccess || results.some(Boolean);
      }
      if (hasSuccess) {
        await onUploadSuccess?.();
      }
    } finally {
      // nothing to do
    }
  }, [fileState, uploadFile, onUploadSuccess]);

  const isPending = fileState.some(
    (selection) => selection.uploadState === 'isPending'
  );

  const isInitial = fileState.every(
    (selection) => selection.uploadState === 'initial'
  );

  const primaryDisabled = isInitial
    ? fileState.length === 0 || isPending
    : isPending;
  const primaryLabel = isInitial
    ? t('app.upload.upload')
    : isPending
      ? t('app.createRow.submitting')
      : t('app.createRow.okay');
  const primaryIcon = isInitial ? (
    <GoogleFontIcon name="upload" />
  ) : isPending ? (
    <GoogleFontIcon name="hourglass_top" />
  ) : (
    <GoogleFontIcon name="check" />
  );

  return (
    <Modal
      actions={
        <>
          <Button
            variant="secondary"
            onClick={cleanupAndClose}
            disabled={!isInitial}
          >
            {t('app.upload.cancel')}
          </Button>
          <Button
            icon={primaryIcon}
            onClick={isInitial ? uploadAllFiles : cleanupAndClose}
            disabled={primaryDisabled}
          >
            {primaryLabel}
          </Button>
        </>
      }
      modalType="action"
      opened={opened}
      onClose={cleanupAndClose}
      size={768}
      title={t('app.upload.zipUploadModal.headline')}
      withinPortal={withinPortal}
    >
      <Upload
        fileState={fileState}
        dispatch={dispatch}
        accept={['application/zip', 'application/x-zip-compressed']}
        uploadFile={uploadSingleFile}
      />
    </Modal>
  );
}
